import { Bookmark, BookOpen, MessageSquareText, Pencil, Target } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useBackState } from "@/book/components/back";
import { useSearch, type SearchHit, type SearchKind } from "@/shared/api/book";
import { track } from "@/shared/lib/track";
import { useFilters } from "@/shared/lib/use-filters";
import { SearchInput } from "@/shared/ui/filters";
import { PageTitle } from "@/shared/ui/page-header";
import { SectionLabel } from "@/shared/ui/section-label";
import { Empty, ErrorNote, ListSkeleton } from "@/shared/ui/state";

const KIND: Record<SearchKind, { label: string; icon: typeof Target; to: (hit: SearchHit) => string }> = {
  exercise: { label: "Gyakorlat", icon: Target, to: (h) => `/app/book/gyakorlatok/${h.id}` },
  reading: { label: "Ajánlás", icon: Bookmark, to: () => "/app/book/ajanlo" },
  experience: {
    label: "Tapasztalat",
    icon: MessageSquareText,
    to: () => "/app/book/tapasztalatok",
  },
  glossary: { label: "Fogalom", icon: BookOpen, to: () => "/app/book/fogalomtar" },
  erratum: { label: "Hibajegyzék", icon: Pencil, to: () => "/app/book/hibajegyzek" },
};

const ORDER: SearchKind[] = ["exercise", "glossary", "reading", "experience", "erratum"];

/** Globális kereső: egy kifejezés, találatok minden tartalomtípusból. */
export function SearchPage() {
  const { values, set } = useFilters({ q: "" });
  const q = values.q;
  const { data, isPending, isError, isFetching } = useSearch(q);
  const ready = q.trim().length >= 2;
  const back = useBackState();
  useTrackSearch(q, ready && data && !isFetching ? data.hits.length : null);

  const grouped = ORDER.map((kind) => ({
    kind,
    hits: (data?.hits ?? []).filter((h) => h.kind === kind),
  })).filter((g) => g.hits.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <PageTitle>Keresés</PageTitle>
      <SearchInput
        label="Keresés a mellékletben"
        placeholder="Keresés a mellékletben"
        value={q}
        onChange={(v) => set({ q: v })}
      />

      {!ready && <Empty>Írj be legalább két betűt.</Empty>}
      {ready && isPending && <ListSkeleton />}
      {ready && isError && <ErrorNote />}
      {ready && data && grouped.length === 0 && !isFetching && (
        <Empty>Erre a keresésre nincs találat.</Empty>
      )}

      {grouped.map(({ kind, hits }) => (
        <section key={kind} className="flex flex-col">
          <SectionLabel className="mb-2">{KIND[kind].label}</SectionLabel>
          {hits.map((hit) => {
            const Icon = KIND[kind].icon;
            return (
              <Link
                key={`${kind}-${hit.id}`}
                to={KIND[kind].to(hit)}
                state={back}
                className="flex items-start gap-3 border-t border-line py-3 hover:bg-paper"
              >
                <Icon size={20} className="mt-0.5 shrink-0 text-gold" strokeWidth={1.8} />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[15px] font-semibold leading-snug">{hit.title}</span>
                  {hit.snippet && (
                    <span className="text-[13px] leading-[1.45] text-muted">{hit.snippet}</span>
                  )}
                  {hit.chapters.length > 0 && (
                    <span className="text-[12px] text-muted">
                      {hit.chapters.map((c) => `${c.number}. fejezet`).join(" · ")}
                    </span>
                  )}
                </span>
              </Link>
            );
          })}
        </section>
      ))}
    </div>
  );
}

/** A keresés a használati statisztikába (rendszerterv 9.1): csak a „megállapodott” kifejezés
 *  — gépelés közben minden betűre keres a lista, ezért 1,5 mp nyugalom után, és ugyanazt
 *  egyszer. A kifejezést a szerver a felhasználótól elválasztva, névtelenül összesíti. */
function useTrackSearch(q: string, results: number | null) {
  const last = useRef("");
  useEffect(() => {
    const term = q.trim();
    if (results === null || term === last.current) return;
    const timer = setTimeout(() => {
      last.current = term;
      track("book.search", null, { q: term, results });
    }, 1500);
    return () => clearTimeout(timer);
  }, [q, results]);
}
